import { createFocusTrap } from "focus-trap";
import classNames from "classnames";
import debounce from "lodash/debounce";
import { pxToRem } from "@castiron/style-mixins";
import Sb from "../abstract/StatefulBehavior";
import checkCanFocusTrap from "../../helpers/checkCanFocusTrap";
import { enhanceAnchorIntoButton } from "../../helpers/dom";

const BREAKPOINT = pxToRem("760px");

export default class ToggleDrawer extends Sb {
  constructor(el, props, refs) {
    super();

    this.el = el;
    this.props = props;
    this.refs = refs;

    this.state = {
      open: false,
      returnId: this.defaultReturnId,
    };

    if (this.shouldDisable) return this.removeDialogFromDOM();

    if (!this.toggles?.length || !this.dialog) return;

    this.initDialogClass = this.dialog.className;
    this.initOverlayClass = this.overlay?.className;
    this.initBodyClass = document.body.className;

    this.trap = createFocusTrap(this.trapContainer, {
      escapeDeactivates: true,
      clickOutsideDeactivates: true,
      setReturnFocus: false,
      onDeactivate: () => {
        if (!this.state.open) return;
        this.setState({ open: false });
      },
      onPostDeactivate: () =>
        document.getElementById(this.state.returnId).focus(),
      checkCanFocusTrap,
    });

    this.update();
    this.bindToggles();
    this.bindRemoteToggle();
    this.bindOverlayClick();
    this.bindResize();
  }

  get toggles() {
    return Array.isArray(this.refs.toggle)
      ? this.refs.toggle
      : [this.refs.toggle];
  }

  get defaultReturnId() {
    return this.toggles[0]?.id;
  }

  get dialog() {
    return this.refs.dialog;
  }

  get overlay() {
    return this.refs.overlay;
  }

  get trapContainer() {
    return this.props.excludeToggleFromTrap ? this.dialog : this.el;
  }

  // Optionally disable toggle behavior on some routes
  // (e.g. to disable cart drawer when on cart page)
  get shouldDisable() {
    if (!this.props.disableOnPage) return false;
    return window.location.pathname === this.props.disableOnPage;
  }

  update = () => {
    this.dialog.setAttribute("data-open", this.state.open);

    this.toggles.forEach((toggle) =>
      toggle.setAttribute("aria-expanded", this.state.open ? "true" : "false"),
    );

    if (this.overlay) {
      this.overlay.className = classNames(this.initOverlayClass, {
        "is-open": this.state.open,
      });
    }

    document.body.classList.toggle("has-scroll-lock", this.state.open);

    this.state.open ? this.trap.activate() : this.trap.deactivate();
  };

  bindToggles() {
    this.toggles.forEach((toggle) => {
      toggle.setAttribute("aria-controls", toggle.dataset.controls);
      enhanceAnchorIntoButton(toggle, () =>
        this.setState({
          open: !this.state.open,
          returnId: this.defaultReturnId,
        }),
      );
    });
  }

  // Allow buttons elsewhere in the DOM to remotely open the drawer
  bindRemoteToggle = () => {
    window.addEventListener("message", (event) => {
      if (event.origin !== window.location.origin) return;

      const { drawerId, drawerAction = "toggle", toggleId } = event.data;
      if (drawerId !== this.dialog.id) return;

      this.setState({
        open: drawerAction === "open" ? true : !this.state.open,
        returnId: toggleId,
      });
    });
  };

  bindOverlayClick() {
    if (!this.overlay) return;

    this.overlay.addEventListener("click", () =>
      this.setState({ open: false }),
    );
  }

  bindResize() {
    if (!this.props.bindResize) return;

    const debouncedClientSize = debounce(() => {
      if (!this.state.open) return;
      // if dialog is open and viewport expands above breakpoint, close it
      if (window.matchMedia(`(min-width: ${BREAKPOINT})`).matches) {
        this.setState({ open: false });
      }
    }, 200);
    window.addEventListener("resize", debouncedClientSize);
  }

  /**
   * For the cart page, having the drawer in the DOM causes
   * expections when dynamically updating cart elements
   */
  removeDialogFromDOM() {
    if (!this.dialog) return;
    this.dialog.remove();
  }
}
